import { useLocationLogo } from "@/modules/platformLocation/useLocationLogo";
import { locationBrandingStyle } from "@/modules/platformLocation/locationBrandingStyle";
import { WalletAvailabilityWarmup } from "@/modules/stripe/WalletAvailabilityWarmup";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useRouteError } from "react-router-dom";

import {
  getAuthClaims,
  getPlatform,
  getPlatformCurrent,
  getPlatformLocation,
  getUser,
} from "api/methods";
import { usePlatformDomainTokens } from "hooks/usePlatformDomainTokens";
import {
  isAnonymousBrowsablePath,
  isAnonymousUser,
  isSessionlessExternalReturnPath,
  setPostLoginReturnTo,
  useIsAnonymousBrowsing,
} from "modules/anonymousBrowsing";
import TopNav from "molecules/navbars/TopNav";
import { DesktopHeader } from "molecules/navigation/desktop-header/DesktopHeader";
import { SiteFooter } from "molecules/navigation/site-footer/SiteFooter";
import { AccessTemporarilySuspendedModal } from "organisms/access-temporarily-suspended-modal/AccessTemporarilySuspendedModal";
import { DisputeInProgressModal } from "organisms/dispute-in-progress-modal/DisputeInProgressModal";
import RootError from "pages/RootError";
import { identifyFlagUser, useFeatureFlag } from "services/Flags";
import { clearAcknowledgedPopupPromotionIds } from "utils/functions";

/**
 * Layout route for authenticated users (post-login)
 */
const AppLayoutNoBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAnonymousBrowsingActive = useIsAnonymousBrowsing();
  const isDesktopExperience = useFeatureFlag("desktopExperience");

  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const { data: platform } = useQuery({
    ...getPlatformCurrent.keyed(),
    staleTime: Infinity,
    // Known: this query throws errors in anonymous contexts on mobile
    useErrorBoundary: false,
  });

  const { data: authClaims } = useQuery({
    ...getAuthClaims.keyed(),
    staleTime: Infinity,
  });

  const { data: platformLocation } = useQuery({
    ...getPlatformLocation.keyed(),
    staleTime: Infinity,
  });

  const locationBranding = useLocationLogo(platform?.id, platformLocation?.id, user?.isGuest);

  const { platformDomainTokens } = usePlatformDomainTokens();

  useEffect(() => {
    if (!user || !platform || isAnonymousUser(user)) return;

    identifyFlagUser(
      {
        id: user.id,
        name: user.phoneNumber,
        email: user.email ?? undefined,
      },
      {
        id: platform.id,
        domain: user.platformDomain?.domain ?? undefined,
        created: dayjs(platform.created).toDate(),
      },
    );
  }, [user, platform]);

  // Dispute modal logic
  const isAdminImpersonation = !!authClaims?.claims?.adminUserId;
  const hasActiveDispute = !isAdminImpersonation && !!user?.hasActiveDispute;
  const hasPartialDisputePendingAdminAction =
    !isAdminImpersonation && !!user?.hasPartialDisputePendingAdminAction;
  const isOnPlatformSelector = location.pathname.startsWith("/platform-selector");
  const showDisputeModal = hasActiveDispute && !isOnPlatformSelector;
  const showSuspendedModal =
    !showDisputeModal && hasPartialDisputePendingAdminAction && !isOnPlatformSelector;
  const platformName = platform?.name ?? user?.platform?.name ?? "";
  const contactPhone = user?.platformLocation?.phoneNumber ?? platformLocation?.phoneNumber;
  const contactEmail = user?.platformLocation?.email ?? platformLocation?.email;
  const showSwitchLocations = (platformDomainTokens?.length ?? 0) > 1;

  // Sibling navigations skip the loader, so the account-only guard also holds at render time.
  if (isAnonymousBrowsingActive && !isAnonymousBrowsablePath(location.pathname)) {
    setPostLoginReturnTo(location.pathname + location.search);
    return <Navigate replace to="/signin" />;
  }

  return (
    <div className="contents" style={locationBrandingStyle(locationBranding?.brandColor)}>
      {platform && <WalletAvailabilityWarmup platform={platform} />}
      <DisputeInProgressModal
        open={showDisputeModal}
        platformName={platformName}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        showSwitchLocations={showSwitchLocations}
        onSwitchLocations={() => navigate("/platform-selector")}
      />
      <AccessTemporarilySuspendedModal
        open={showSuspendedModal}
        platformName={platformName}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        showSwitchLocations={showSwitchLocations}
        onSwitchLocations={() => navigate("/platform-selector")}
      />

      {isDesktopExperience && <DesktopHeader />}
      <div className="flex flex-auto">
        <div className="mx-auto w-full max-w-screen-xl desk2xl:max-w-screen-2xl">
          <Outlet />
        </div>
      </div>
      {isDesktopExperience && <SiteFooter />}
    </div>
  );
};

/** A bare `<Outlet />` for the sessionless external return, so the layout's auth queries never mount. */
export const AppLayoutNoBottomNavOrBareExternalReturn = () => {
  const location = useLocation();
  if (isSessionlessExternalReturnPath(location.pathname, location.search)) {
    return <Outlet />;
  }

  return <AppLayoutNoBottomNav />;
};

export const AppLayoutErrorNoBottomNav = () => {
  const error = useRouteError();
  const location = useLocation();

  const { data: logo } = useQuery({
    ...getPlatform.keyed(),
    select: (data) => data.logoImageUrl,
  });

  if (isAxiosError(error) && error.response?.status === 401) {
    clearAcknowledgedPopupPromotionIds();
    setPostLoginReturnTo(location.pathname + location.search);
    return <Navigate to="/signin" />;
  }

  return (
    <>
      <DesktopHeader />
      <div className="w-full">
        <TopNav>{logo && <img src={logo} className="h-8" />}</TopNav>
        <RootError />
      </div>
    </>
  );
};

export default AppLayoutNoBottomNav;
